import { Play, Trash2 } from "lucide-react";

import { isVideo } from "@/lib/media";
import { cn } from "@/lib/utils";

/**
 * One photo or video tile. Videos show their first frame (the `#t=0.1`
 * fragment makes iOS render a frame instead of a blank box) with a play badge;
 * tapping opens the file in a new tab where the phone's own player takes over.
 */
export function MediaThumb({
  url,
  mimeType,
  caption,
  className,
  onDelete,
  disabled,
}: {
  url: string;
  mimeType?: string | null;
  caption?: string | null;
  className?: string;
  onDelete?: () => void;
  disabled?: boolean;
}) {
  const video = isVideo(mimeType);
  return (
    <div className={cn("group relative overflow-hidden rounded-lg border border-white/15 bg-black/30", className ?? "h-20 w-20")}>
      <a href={url} target="_blank" rel="noreferrer" title={caption ?? ""} className="block h-full w-full">
        {video ? (
          <>
            <video src={`${url}#t=0.1`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-night/70 p-1.5 text-paper">
                <Play className="h-3.5 w-3.5 fill-current" />
              </span>
            </span>
          </>
        ) : (
          <img src={url} alt={caption ?? ""} className="h-full w-full object-cover" loading="lazy" />
        )}
      </a>
      {onDelete && (
        <button
          type="button"
          aria-label="Delete"
          className="absolute right-1 top-1 rounded-full bg-night/80 p-1 text-ice/80 opacity-0 transition group-hover:opacity-100 hover:text-pink focus:opacity-100"
          disabled={disabled}
          onClick={onDelete}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
